import { useMemo } from "react";

import type { Chart as HighchartsChart, Point as HighchartsPoint } from "highcharts";
import isNil from "lodash/isNil";
import ReactDOMServer from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";

import { translate } from "app/filters/i18nFilter";
import { percentageFilter } from "app/filters/ngFilters";
import { TOP_RANK } from "app/common/benchmark/constants";
import type { TrafficShareMetricSchema } from "app/common/benchmark/types";

import { ChartTooltip } from "./ChartTooltip/ChartTooltip";
import { colors } from "./pieChartColors";
import { createOrderedData } from "./createOrderedData";

const DEFAULT_PERCENTAGE = 0;
const PERCENTAGE_FRACTION_DIGITS = 2;

type PieChartSchema = {
    primary: string;
    trafficShareData: TrafficShareMetricSchema;
    activeIndex?: number | null;
    sorted?: boolean;
    onPointHover?: (index: number) => void;
};

export const usePieChart = ({ primary, activeIndex, trafficShareData, sorted, onPointHover }: PieChartSchema) => {
    const competitorsData = trafficShareData?.competitorsData;
    const primaryData = trafficShareData?.primaryData;
    const withOthers = !!trafficShareData?.othersData;
    const isPrimaryInTop = trafficShareData?.primaryData?.rank <= TOP_RANK;

    const chartData = useMemo(() => {
        if (!competitorsData || !primaryData) {
            return [];
        }

        const primaryChartData = {
            name: primary,
            y: +percentageFilter()(primaryData.share, PERCENTAGE_FRACTION_DIGITS),
            color: colors[0],
            type: "pie",
            isPrimary: true,
            rank: primaryData.rank,
        };

        const competitorsChartData = Object.keys(competitorsData).map((competitor, index) => {
            const percentage = percentageFilter()(
                competitorsData[competitor]?.share ?? DEFAULT_PERCENTAGE,
                PERCENTAGE_FRACTION_DIGITS,
            );

            return {
                name: competitor,
                y: +percentage,
                color: colors[index + 1],
                type: "pie",
                isPrimary: false,
                rank: competitorsData[competitor].rank,
            };
        });

        const otherData = {
            name: translate("bt.metrics.share.cell.others"),
            y: +percentageFilter()(
                trafficShareData?.othersData?.share ?? DEFAULT_PERCENTAGE,
                PERCENTAGE_FRACTION_DIGITS,
            ),
            color: colorsPalettes.carbon[100],
            type: "pie",
            isPrimary: false,
            rank: TOP_RANK,
        };

        return [
            {
                data: createOrderedData({
                    primaryData: primaryChartData,
                    competitorsData: competitorsChartData,
                    withOthers,
                    otherData,
                    isPrimaryInTop,
                    sorted,
                }),
            },
        ];
    }, [competitorsData, isPrimaryInTop, primary, primaryData, sorted, trafficShareData, withOthers]);

    const chartConfig = useMemo(() => {
        return {
            chart: {
                animation: false,
                type: "pie",
                plotBackgroundColor: "transparent",
                width: 200,
                height: 196,
                textAlign: "center",
                events: {
                    redraw: function (this: HighchartsChart) {
                        if (isNil(activeIndex)) {
                            return;
                        }

                        const point = this.series[0]?.points[activeIndex];

                        point?.setState("hover", true);
                    },
                },
            },
            plotOptions: {
                pie: {
                    innerSize: "60%",
                    animation: false,
                    cursor: "pointer",
                    borderWidth: 0,
                    borderRadius: 0,
                    dataLabels: {
                        enabled: false,
                    },
                    states: {
                        hover: {
                            enabled: true,
                            brightness: 0.05,
                        },
                    },
                    point: {
                        events: {
                            mouseOver: function (this: HighchartsPoint) {
                                onPointHover?.(this.index);
                            },
                        },
                    },
                },
            },

            tooltip: {
                outside: true,
                useHTML: true,
                formatter(this: HighchartsPoint) {
                    return ReactDOMServer.renderToString(
                        <ChartTooltip
                            name={String(this.key)}
                            value={`${this.y}%`}
                            color={String(this.color)}
                        />,
                    );
                },
            },
        };
    }, [activeIndex]);

    return { chartData, chartConfig };
};
